import React from 'react'
import {useTranslation} from 'react-i18next'
import {useParams, useLocation, useHistory} from 'react-router-dom'

import {Box, Container, Grid} from '@mui/material'
import {getProductLineColors} from '@progynyglobal/pg-ui-library'

import {BackButton} from '@progyny/components/Buttons'
import JumboTitle from '@progyny/components/JumboTitle'
import PageLoader from '@progyny/components/PageLoader'
import {useConsultationsProgramDetailQuery} from '@progyny/generated/graphql'
import {useProductLine} from '@progyny/hooks/useProductLine'
import SessionPlan from '@progyny/pages/Consultations/Program/components/SessionPlan'
import SingleSessionOverview from '@progyny/pages/Consultations/components/SingleSession/SingleSessionOverview'
import {useMeStore} from '@progyny/store/meStore'

interface ProgramParams {
  program: string
}

const Program: React.ComponentType = () => {
  const {program: programSlug} = useParams<ProgramParams>()
  const {productLine, isFertility} = useProductLine()
  const colors = getProductLineColors(productLine)
  const location = useLocation()
  const user = useMeStore((s) => s.user)
  const {t} = useTranslation()
  const history = useHistory()

  const isLanguageChanged =
    user?.member?.language !== localStorage.getItem('ui_language')

  const {data, loading, error, refetch} = useConsultationsProgramDetailQuery({
    variables: {slug: programSlug},
    skip: !programSlug,
  })

  const [isCalendarVisible, setIsCalendarVisible] = React.useState(false)
  const [isTabsVisible, setIsTabsVisible] = React.useState(true)

  const handleBackClick = () => {
    history.push(`/consultations`)
  }
  // Refetch when returning from booking page to show updated appointment details
  React.useEffect(() => {
    // Immediate refetch
    refetch()

    // Also refetch after a short delay to account for webhook processing time
    const timeoutId = setTimeout(() => {
      refetch()
    }, 2000)

    return () => clearTimeout(timeoutId)
  }, [location.pathname, refetch, isLanguageChanged])

  if (loading) {
    return (
      <Box minHeight={140}>
        <PageLoader position={'center'} />
      </Box>
    )
  }

  if (error || !data?.consultations?.program) {
    return null
  }

  const program = data.consultations.program
  const isAskAMidwifeProgram = programSlug.endsWith('ask-a-midwife')

  // For fertility-consultation, use program.consultation.title instead of program.title
  const displayTitle =
    programSlug === 'fertility-consultation'
      ? program?.consultation?.title
      : program.title
  const displayDescription =
    programSlug === 'fertility-consultation'
      ? program?.consultation?.description
      : program.description

  return (
    <Container maxWidth={'xl'}>
      <JumboTitle
        title={isCalendarVisible ? undefined : displayTitle}
        description={isCalendarVisible ? undefined : displayDescription}
        forceDefaultHeight={isCalendarVisible}
        disableContentWrapper={isCalendarVisible || !isTabsVisible}
      >
        {!isFertility && !isCalendarVisible && (
          <BackButton
            style={{marginBottom: '64px'}}
            text={t('consultations_onboarding_back_to_overview')}
            onBackClick={handleBackClick}
          />
        )}
        <Grid container>
          {isAskAMidwifeProgram ? (
            <SingleSessionOverview
              programSlug={programSlug}
              colors={colors}
              onCalendarVisibilityChange={setIsCalendarVisible}
              onTabsVisibilityChange={setIsTabsVisible}
            />
          ) : (
            <SessionPlan
              colors={colors}
              onCalendarVisibilityChange={setIsCalendarVisible}
              onTabsVisibilityChange={setIsTabsVisible}
            />
          )}
        </Grid>
      </JumboTitle>
    </Container>
  )
}

export default Program
